/**
 * Announcement bar behaviour (TPN round 4 item 16).
 *
 * The inline script in components/announcement-bar.php has already decided
 * whether the bar shows (session dismissal) and set --announcement-bar-h
 * before first paint. This class keeps the layout right afterwards:
 *
 * - Height: the bar wraps differently as the viewport changes, so a
 *   ResizeObserver keeps --announcement-bar-h equal to its real height.
 * - Scroll: --announcement-bar-visible is the part of the bar still on
 *   screen (height minus scroll, never below 0). The bar slides up with the
 *   page and the fixed header follows it to the top, so after a short scroll
 *   the page behaves exactly as it does without a bar.
 * - Close: hides the bar, removes the layout offsets and remembers the
 *   announcement id in sessionStorage so it stays closed for the session.
 *   The id changes when the message or link changes.
 */
export default class AnnouncementBar {
	/**
	 * @param {HTMLElement} el The .announcement-bar element.
	 */
	constructor(el) {
		this.el = el;
		this.root = document.documentElement;
		this.closeButton = el.querySelector('.announcement-bar__close');
		this.height = 0;
		this.ticking = false;

		this.onScroll = this.onScroll.bind(this);
		this.update = this.update.bind(this);

		this.measure();
		this.update();

		if ('ResizeObserver' in window) {
			this.resizeObserver = new ResizeObserver(() => {
				this.measure();
				this.update();
			});
			this.resizeObserver.observe(this.el);
		} else {
			window.addEventListener('resize', () => {
				this.measure();
				this.update();
			});
		}

		window.addEventListener('scroll', this.onScroll, { passive: true });

		if (this.closeButton) {
			this.closeButton.addEventListener('click', () => this.close());
		}
	}

	/** Read the bar's current height and publish it as --announcement-bar-h. */
	measure() {
		// Fractional height, not offsetHeight: rounding leaves a 1px sliver of
		// page between the bar and the header when the bar is 56.7px tall.
		this.height = this.el.getBoundingClientRect().height;
		this.root.style.setProperty('--announcement-bar-h', `${this.height}px`);
	}

	/** Coalesce scroll events into one update per animation frame. */
	onScroll() {
		if (this.ticking) return;
		this.ticking = true;
		requestAnimationFrame(this.update);
	}

	/** Publish how much of the bar is still visible at the current scroll position. */
	update() {
		this.ticking = false;
		const visible = Math.max(0, this.height - window.scrollY);
		this.root.style.setProperty('--announcement-bar-visible', `${visible}px`);
	}

	/** Close for this session and give the space back to the page. */
	close() {
		try {
			sessionStorage.setItem('tpnAnnouncementClosed', this.el.dataset.announcementId || '');
		} catch (e) {
			// Private browsing can block storage; the bar still closes for this page view.
		}

		window.removeEventListener('scroll', this.onScroll);
		if (this.resizeObserver) this.resizeObserver.disconnect();

		this.root.classList.remove('has-announcement-bar');
		this.root.style.removeProperty('--announcement-bar-h');
		this.root.style.removeProperty('--announcement-bar-visible');
		this.el.remove();

		// Keep keyboard users in a sensible place after the button disappears.
		const logoLink = document.querySelector('.site-header a');
		if (logoLink) logoLink.focus({ preventScroll: true });
	}
}
